// src/seller/Esp32Builder.jsx

import {
    useEffect,
    useState,
} from 'react';

import {
    useSearchParams,
    useNavigate,
} from 'react-router-dom';

import Editor from '@monaco-editor/react';
import Esp32NutrientBuilder from './Esp32NutrientBuilder';


const API_BASE =
    '/api/seller/smartfarm/esp32/builder';


// ============================================================
// 선택형 자동 장치 카탈로그
//
// ESPHome에서 많이 사용하는 센서/입력/스위치/출력/통신 장치를
// Builder에서 선택하여 packages 블록으로 자동 생성한다.
//
// 장치별 package를 사용하므로 기존 sensor:/binary_sensor:/switch:
// 블록을 직접 문자열 병합하지 않아도 ESPHome이 설정을 병합한다.
// ============================================================

const AUTO_START = '# KONGGOMUL AUTO DEVICES START';
const AUTO_END = '# KONGGOMUL AUTO DEVICES END';
const AUTO_META = '# KONGGOMUL_DEVICE_CONFIG:';

const DEVICE_CATALOG = [
    { category: '온도 / 습도 / 기압', type: 'dht', label: 'DHT11 / DHT22 / AM2302', fields: [
        { key: 'name', label: '이름', default: '온습도' },
        { key: 'pin', label: 'GPIO', default: 'GPIO27' },
        { key: 'model', label: '모델', type: 'select', default: 'DHT22', options: ['DHT11', 'DHT22', 'AM2302', 'AUTO_DETECT'] },
        { key: 'interval', label: '주기', default: '10s' },
    ]},
    { category: '온도 / 습도 / 기압', type: 'dallas', label: 'DS18B20 / 1-Wire 온도', fields: [
        { key: 'name', label: '이름', default: 'DS18B20 온도' },
        { key: 'pin', label: '1-Wire GPIO', default: 'GPIO4' },
        { key: 'interval', label: '주기', default: '30s' },
    ]},
    { category: '온도 / 습도 / 기압', type: 'bme280', label: 'BME280 온습도/기압 (I²C)', fields: [
        { key: 'name', label: '이름', default: 'BME280' }, { key: 'sda', label: 'SDA', default: 'GPIO21' }, { key: 'scl', label: 'SCL', default: 'GPIO22' },
        { key: 'address', label: '주소', default: '0x76' }, { key: 'interval', label: '주기', default: '30s' },
    ]},
    { category: '온도 / 습도 / 기압', type: 'bmp280', label: 'BMP280 온도/기압 (I²C)', fields: [
        { key: 'name', label: '이름', default: 'BMP280' }, { key: 'sda', label: 'SDA', default: 'GPIO21' }, { key: 'scl', label: 'SCL', default: 'GPIO22' },
        { key: 'address', label: '주소', default: '0x76' }, { key: 'interval', label: '주기', default: '30s' },
    ]},
    { category: '온도 / 습도 / 기압', type: 'sht3xd', label: 'SHT30 / SHT31 / SHT35 (I²C)', fields: [
        { key: 'name', label: '이름', default: 'SHT3x' }, { key: 'sda', label: 'SDA', default: 'GPIO21' }, { key: 'scl', label: 'SCL', default: 'GPIO22' },
        { key: 'address', label: '주소', default: '0x44' }, { key: 'interval', label: '주기', default: '30s' },
    ]},
    { category: '온도 / 습도 / 기압', type: 'sht4x', label: 'SHT40 / SHT41 / SHT45 (I²C)', fields: [
        { key: 'name', label: '이름', default: 'SHT4x' }, { key: 'sda', label: 'SDA', default: 'GPIO21' }, { key: 'scl', label: 'SCL', default: 'GPIO22' },
        { key: 'address', label: '주소', default: '0x44' }, { key: 'interval', label: '주기', default: '30s' },
    ]},
    { category: '온도 / 습도 / 기압', type: 'aht10', label: 'AHT10 / AHT20 (I²C)', fields: [
        { key: 'name', label: '이름', default: 'AHT 온습도' }, { key: 'sda', label: 'SDA', default: 'GPIO21' }, { key: 'scl', label: 'SCL', default: 'GPIO22' },
        { key: 'interval', label: '주기', default: '30s' },
    ]},
    { category: '온도 / 습도 / 기압', type: 'hdc1080', label: 'HDC1080 온습도 (I²C)', fields: [
        { key: 'name', label: '이름', default: 'HDC1080' }, { key: 'sda', label: 'SDA', default: 'GPIO21' }, { key: 'scl', label: 'SCL', default: 'GPIO22' },
        { key: 'interval', label: '주기', default: '30s' },
    ]},
    { category: '온도 / 습도 / 기압', type: 'bme680', label: 'BME680 온습도/기압/가스 (I²C)', fields: [
        { key: 'name', label: '이름', default: 'BME680' }, { key: 'sda', label: 'SDA', default: 'GPIO21' }, { key: 'scl', label: 'SCL', default: 'GPIO22' },
        { key: 'address', label: '주소', default: '0x77' }, { key: 'interval', label: '주기', default: '30s' },
    ]},

    { category: '토양 / 수위 / 거리 / 유량', type: 'soil_adc', label: '정전식/아날로그 토양수분 (ADC)', fields: [
        { key: 'name', label: '이름', default: '토양 수분' }, { key: 'pin', label: 'ADC GPIO', default: 'GPIO34' },
        { key: 'dry', label: '건조 전압', default: '3.00' }, { key: 'wet', label: '습윤 전압', default: '1.20' }, { key: 'interval', label: '주기', default: '10s' },
    ]},
    { category: '토양 / 수위 / 거리 / 유량', type: 'ultrasonic', label: 'HC-SR04 / JSN-SR04T 초음파', fields: [
        { key: 'name', label: '이름', default: '수위 거리' }, { key: 'trigger', label: 'Trigger', default: 'GPIO5' }, { key: 'echo', label: 'Echo', default: 'GPIO18' },
        { key: 'timeout', label: '최대거리', default: '4m' }, { key: 'interval', label: '주기', default: '5s' },
    ]},
    { category: '토양 / 수위 / 거리 / 유량', type: 'pulse_counter', label: '유량계 / 펄스 카운터', fields: [
        { key: 'name', label: '이름', default: '유량 펄스' }, { key: 'pin', label: 'GPIO', default: 'GPIO26' },
        { key: 'factor', label: '환산계수', default: '1.0' }, { key: 'unit', label: '단위', default: 'pulse/min' }, { key: 'interval', label: '주기', default: '10s' },
    ]},

    { category: '조도 / 환경', type: 'bh1750', label: 'BH1750 조도 (I²C)', fields: [
        { key: 'name', label: '이름', default: '조도' }, { key: 'sda', label: 'SDA', default: 'GPIO21' }, { key: 'scl', label: 'SCL', default: 'GPIO22' },
        { key: 'address', label: '주소', default: '0x23' }, { key: 'interval', label: '주기', default: '30s' },
    ]},
    { category: '조도 / 환경', type: 'veml7700', label: 'VEML7700 조도 (I²C)', fields: [
        { key: 'name', label: '이름', default: 'VEML7700 조도' }, { key: 'sda', label: 'SDA', default: 'GPIO21' }, { key: 'scl', label: 'SCL', default: 'GPIO22' },
        { key: 'interval', label: '주기', default: '30s' },
    ]},
    { category: '조도 / 환경', type: 'tsl2561', label: 'TSL2561 조도 (I²C)', fields: [
        { key: 'name', label: '이름', default: 'TSL2561 조도' }, { key: 'sda', label: 'SDA', default: 'GPIO21' }, { key: 'scl', label: 'SCL', default: 'GPIO22' },
        { key: 'address', label: '주소', default: '0x39' }, { key: 'interval', label: '주기', default: '30s' },
    ]},

    { category: '공기질 / CO₂ / 미세먼지', type: 'ccs811', label: 'CCS811 eCO₂/TVOC (I²C)', fields: [
        { key: 'name', label: '이름', default: 'CCS811' }, { key: 'sda', label: 'SDA', default: 'GPIO21' }, { key: 'scl', label: 'SCL', default: 'GPIO22' },
        { key: 'address', label: '주소', default: '0x5A' }, { key: 'interval', label: '주기', default: '60s' },
    ]},
    { category: '공기질 / CO₂ / 미세먼지', type: 'sgp30', label: 'SGP30 eCO₂/TVOC (I²C)', fields: [
        { key: 'name', label: '이름', default: 'SGP30' }, { key: 'sda', label: 'SDA', default: 'GPIO21' }, { key: 'scl', label: 'SCL', default: 'GPIO22' },
        { key: 'interval', label: '주기', default: '60s' },
    ]},
    { category: '공기질 / CO₂ / 미세먼지', type: 'sgp40', label: 'SGP40 VOC (I²C)', fields: [
        { key: 'name', label: '이름', default: 'SGP40 VOC' }, { key: 'sda', label: 'SDA', default: 'GPIO21' }, { key: 'scl', label: 'SCL', default: 'GPIO22' },
        { key: 'interval', label: '주기', default: '60s' },
    ]},
    { category: '공기질 / CO₂ / 미세먼지', type: 'ens160', label: 'ENS160 공기질 (I²C)', fields: [
        { key: 'name', label: '이름', default: 'ENS160' }, { key: 'sda', label: 'SDA', default: 'GPIO21' }, { key: 'scl', label: 'SCL', default: 'GPIO22' },
        { key: 'address', label: '주소', default: '0x53' }, { key: 'interval', label: '주기', default: '60s' },
    ]},
    { category: '공기질 / CO₂ / 미세먼지', type: 'mhz19', label: 'MH-Z19 CO₂ (UART)', fields: [
        { key: 'name', label: '이름', default: 'CO2' }, { key: 'tx', label: 'ESP TX', default: 'GPIO17' }, { key: 'rx', label: 'ESP RX', default: 'GPIO16' },
        { key: 'interval', label: '주기', default: '60s' },
    ]},
    { category: '공기질 / CO₂ / 미세먼지', type: 'pmsx003', label: 'PMS5003/PMS7003 미세먼지 (UART)', fields: [
        { key: 'name', label: '이름', default: '미세먼지' }, { key: 'rx', label: 'ESP RX', default: 'GPIO16' },
    ]},
    { category: '공기질 / CO₂ / 미세먼지', type: 'sds011', label: 'SDS011 미세먼지 (UART)', fields: [
        { key: 'name', label: '이름', default: 'SDS011' }, { key: 'tx', label: 'ESP TX', default: 'GPIO17' }, { key: 'rx', label: 'ESP RX', default: 'GPIO16' },
        { key: 'interval', label: '주기', default: '60s' },
    ]},

    { category: '전력 / 전압 / 전류', type: 'ina219', label: 'INA219 전압/전류/전력 (I²C)', fields: [
        { key: 'name', label: '이름', default: 'INA219' }, { key: 'sda', label: 'SDA', default: 'GPIO21' }, { key: 'scl', label: 'SCL', default: 'GPIO22' },
        { key: 'address', label: '주소', default: '0x40' }, { key: 'interval', label: '주기', default: '10s' },
    ]},
    { category: '전력 / 전압 / 전류', type: 'ina226', label: 'INA226 전압/전류/전력 (I²C)', fields: [
        { key: 'name', label: '이름', default: 'INA226' }, { key: 'sda', label: 'SDA', default: 'GPIO21' }, { key: 'scl', label: 'SCL', default: 'GPIO22' },
        { key: 'address', label: '주소', default: '0x40' }, { key: 'shunt', label: 'Shunt Ω', default: '0.1' }, { key: 'maxCurrent', label: '최대 A', default: '3.2' }, { key: 'interval', label: '주기', default: '10s' },
    ]},
    { category: '전력 / 전압 / 전류', type: 'pzemac', label: 'PZEM-004T V3 AC 전력 (UART/Modbus)', fields: [
        { key: 'name', label: '이름', default: 'PZEM' }, { key: 'tx', label: 'ESP TX', default: 'GPIO17' }, { key: 'rx', label: 'ESP RX', default: 'GPIO16' },
        { key: 'address', label: 'Modbus 주소', default: '1' }, { key: 'interval', label: '주기', default: '10s' },
    ]},

    { category: '디지털 입력 / 감지', type: 'pir', label: 'PIR 움직임 감지', fields: [
        { key: 'name', label: '이름', default: '움직임' }, { key: 'pin', label: 'GPIO', default: 'GPIO26' },
        { key: 'delayedOff', label: 'OFF 지연', default: '2s' },
    ]},
    { category: '디지털 입력 / 감지', type: 'gpio_binary', label: '일반 GPIO 디지털 입력', fields: [
        { key: 'name', label: '이름', default: '디지털 입력' }, { key: 'pin', label: 'GPIO', default: 'GPIO26' },
        { key: 'pullup', label: 'Pull-up', type: 'select', default: 'true', options: ['true', 'false'] }, { key: 'inverted', label: '반전', type: 'select', default: 'false', options: ['false', 'true'] },
    ]},
    { category: '디지털 입력 / 감지', type: 'reed', label: '문/창문 리드 스위치', fields: [
        { key: 'name', label: '이름', default: '접점 센서' }, { key: 'pin', label: 'GPIO', default: 'GPIO26' },
    ]},
    { category: '디지털 입력 / 감지', type: 'float', label: '플로트 수위 스위치', fields: [
        { key: 'name', label: '이름', default: '수위 스위치' }, { key: 'pin', label: 'GPIO', default: 'GPIO26' },
        { key: 'inverted', label: '반전', type: 'select', default: 'false', options: ['false', 'true'] },
    ]},
    { category: '디지털 입력 / 감지', type: 'rain', label: '빗물/누수 디지털 센서', fields: [
        { key: 'name', label: '이름', default: '빗물 감지' }, { key: 'pin', label: 'GPIO', default: 'GPIO26' },
        { key: 'inverted', label: '반전', type: 'select', default: 'false', options: ['false', 'true'] },
    ]},

    { category: '릴레이 / 스위치 / 출력', type: 'relay', label: 'GPIO 릴레이 (MQTT 원격제어 포함)', fields: [
        { key: 'name', label: '이름', default: '릴레이' }, { key: 'pin', label: 'GPIO', default: 'GPIO25' },
        { key: 'inverted', label: 'Active LOW', type: 'select', default: 'false', options: ['false', 'true'] },
        { key: 'channel', label: 'MQTT 채널명', default: 'relay1' },
    ]},
    { category: '릴레이 / 스위치 / 출력', type: 'gpio_switch', label: '일반 GPIO 스위치', fields: [
        { key: 'name', label: '이름', default: 'GPIO 스위치' }, { key: 'pin', label: 'GPIO', default: 'GPIO25' },
        { key: 'inverted', label: '반전', type: 'select', default: 'false', options: ['false', 'true'] },
    ]},
    { category: '릴레이 / 스위치 / 출력', type: 'pwm_light', label: 'PWM 조명/디머 (LEDC)', fields: [
        { key: 'name', label: '이름', default: 'PWM 조명' }, { key: 'pin', label: 'GPIO', default: 'GPIO25' }, { key: 'frequency', label: '주파수', default: '1000Hz' },
    ]},
    { category: '릴레이 / 스위치 / 출력', type: 'pwm_fan', label: 'PWM 팬 속도 제어', fields: [
        { key: 'name', label: '이름', default: '환기 팬' }, { key: 'pin', label: 'PWM GPIO', default: 'GPIO25' }, { key: 'frequency', label: '주파수', default: '25000Hz' },
    ]},

    { category: '산업용 / RS485 / Modbus', type: 'sen0601', label: 'DFRobot SEN0601 토양 수분/온도/EC (RS485)', fields: [
        { key: 'tx', label: 'ESP TX', default: 'GPIO17' }, { key: 'rx', label: 'ESP RX', default: 'GPIO16' },
        { key: 'address', label: 'Slave 주소', default: '1' }, { key: 'interval', label: '주기', default: '10s' },
    ]},

    { category: '산업용 / RS485 / Modbus', type: 'modbus_sensor', label: '일반 RS485 Modbus Register 센서', fields: [
        { key: 'name', label: '이름', default: 'Modbus 센서' }, { key: 'tx', label: 'ESP TX', default: 'GPIO17' }, { key: 'rx', label: 'ESP RX', default: 'GPIO16' },
        { key: 'baud', label: 'Baud', default: '9600' }, { key: 'address', label: 'Slave 주소', default: '1' },
        { key: 'register', label: 'Register 주소', default: '0' }, { key: 'registerType', label: 'Register 종류', type: 'select', default: 'holding', options: ['holding', 'read'] },
        { key: 'valueType', label: '값 형식', type: 'select', default: 'U_WORD', options: ['U_WORD', 'S_WORD', 'U_DWORD', 'S_DWORD', 'FP32', 'FP32_R'] },
        { key: 'factor', label: '배율', default: '1.0' }, { key: 'unit', label: '단위', default: '' }, { key: 'interval', label: '주기', default: '10s' },
    ]},

    { category: '고급', type: 'custom_package', label: '사용자 정의 ESPHome package YAML', fields: [
        { key: 'name', label: '이름', default: '사용자 장치' },
        { key: 'yaml', label: 'Package YAML', type: 'textarea', default: 'sensor:\n  - platform: template\n    name: "사용자 센서"\n    lambda: return 0.0;\n    update_interval: 60s' },
    ]},
];

const yamlQuote = (value) =>
    String(value ?? '')
        .replace(/\\/g, '\\\\')
        .replace(/"/g, '\\"')
        .replace(/\r?\n/g, ' ');

const safeId = (value) =>
    String(value || 'device')
        .toLowerCase()
        .replace(/[^a-z0-9_]+/g, '_')
        .replace(/^_+|_+$/g, '') || 'device';

const indentYaml = (text, spaces) => {
    const prefix = ' '.repeat(spaces);
    return String(text || '')
        .split('\n')
        .map(line => line ? prefix + line : line)
        .join('\n');
};

const makeDevicePackage = (device, siteId, deviceId, boardVariant = 'esp32') => {
    const v = device.values || {};
    const uid = safeId(device.key);
    const id = `kg_${uid}`;
    const name = yamlQuote(v.name || device.label || '장치');
    const topic = `smartfarm/${siteId}/${deviceId}`;
    const i2cId = `i2c_${safeId(`${v.sda || 'GPIO21'}_${v.scl || 'GPIO22'}`)}`;
    const i2c = (body) => body;
    const onboardRs485 = boardVariant === 'esp32s3' && v.tx === 'GPIO17' && v.rx === 'GPIO18';
    const uart = (baud, body, tx = v.tx, rx = v.rx) => `uart:\n  - id: uart_${uid}\n${tx ? `    tx_pin: ${tx}\n` : ''}${rx ? `    rx_pin: ${rx}\n` : ''}${onboardRs485 ? '    flow_control_pin: GPIO21\n' : ''}    baud_rate: ${baud}\n\n${body}`;

    switch (device.type) {
        case 'dht':
            return `sensor:\n  - platform: dht\n    pin:\n      number: ${v.pin}\n      mode:\n        input: true\n        pullup: true\n    model: ${v.model}\n    temperature:\n      name: "${name} Temperature"\n      id: ${id}_temperature\n      accuracy_decimals: 1\n    humidity:\n      name: "${name} Humidity"\n      id: ${id}_humidity\n      accuracy_decimals: 1\n    update_interval: ${v.interval}`;
        case 'dallas':
            return `one_wire:\n  - platform: gpio\n    pin: ${v.pin}\n    id: onewire_${uid}\n\nsensor:\n  - platform: dallas_temp\n    one_wire_id: onewire_${uid}\n    name: "${name}"\n    id: ${id}_temperature\n    update_interval: ${v.interval}`;
        case 'bme280':
            return i2c(`sensor:\n  - platform: bme280_i2c\n    i2c_id: ${i2cId}\n    address: ${v.address}\n    temperature:\n      name: "${name} 온도"\n    pressure:\n      name: "${name} 기압"\n    humidity:\n      name: "${name} 습도"\n    update_interval: ${v.interval}`);
        case 'bmp280':
            return i2c(`sensor:\n  - platform: bmp280_i2c\n    i2c_id: ${i2cId}\n    address: ${v.address}\n    temperature:\n      name: "${name} 온도"\n    pressure:\n      name: "${name} 기압"\n    update_interval: ${v.interval}`);
        case 'sht3xd':
            return i2c(`sensor:\n  - platform: sht3xd\n    i2c_id: ${i2cId}\n    address: ${v.address}\n    temperature:\n      name: "${name} 온도"\n    humidity:\n      name: "${name} 습도"\n    update_interval: ${v.interval}`);
        case 'sht4x':
            return i2c(`sensor:\n  - platform: sht4x\n    i2c_id: ${i2cId}\n    address: ${v.address}\n    temperature:\n      name: "${name} 온도"\n    humidity:\n      name: "${name} 습도"\n    update_interval: ${v.interval}`);
        case 'aht10':
            return i2c(`sensor:\n  - platform: aht10\n    i2c_id: ${i2cId}\n    temperature:\n      name: "${name} 온도"\n    humidity:\n      name: "${name} 습도"\n    update_interval: ${v.interval}`);
        case 'hdc1080':
            return i2c(`sensor:\n  - platform: hdc1080\n    i2c_id: ${i2cId}\n    temperature:\n      name: "${name} 온도"\n    humidity:\n      name: "${name} 습도"\n    update_interval: ${v.interval}`);
        case 'bme680':
            return i2c(`sensor:\n  - platform: bme680\n    i2c_id: ${i2cId}\n    address: ${v.address}\n    temperature:\n      name: "${name} 온도"\n    pressure:\n      name: "${name} 기압"\n    humidity:\n      name: "${name} 습도"\n    gas_resistance:\n      name: "${name} 가스 저항"\n    update_interval: ${v.interval}`);
        case 'soil_adc':
            return `sensor:\n  - platform: adc\n    pin: ${v.pin}\n    name: "${name}"\n    id: ${id}_soil\n    attenuation: 12db\n    update_interval: ${v.interval}\n    unit_of_measurement: "%"\n    accuracy_decimals: 1\n    filters:\n      - calibrate_linear:\n          - ${v.dry} -> 0.0\n          - ${v.wet} -> 100.0\n      - clamp:\n          min_value: 0\n          max_value: 100`;
        case 'ultrasonic':
            return `sensor:\n  - platform: ultrasonic\n    trigger_pin: ${v.trigger}\n    echo_pin: ${v.echo}\n    name: "${name}"\n    id: ${id}_distance\n    timeout: ${v.timeout}\n    update_interval: ${v.interval}`;
        case 'pulse_counter':
            return `sensor:\n  - platform: pulse_counter\n    pin: ${v.pin}\n    name: "${name}"\n    id: ${id}_pulse\n    unit_of_measurement: "${yamlQuote(v.unit)}"\n    update_interval: ${v.interval}\n    filters:\n      - multiply: ${v.factor}`;
        case 'bh1750':
            return i2c(`sensor:\n  - platform: bh1750\n    i2c_id: ${i2cId}\n    address: ${v.address}\n    name: "${name}"\n    update_interval: ${v.interval}`);
        case 'veml7700':
            return i2c(`sensor:\n  - platform: veml7700\n    i2c_id: ${i2cId}\n    ambient_light:\n      name: "${name}"\n    update_interval: ${v.interval}`);
        case 'tsl2561':
            return i2c(`sensor:\n  - platform: tsl2561\n    i2c_id: ${i2cId}\n    address: ${v.address}\n    name: "${name}"\n    update_interval: ${v.interval}`);
        case 'ccs811':
            return i2c(`sensor:\n  - platform: ccs811\n    i2c_id: ${i2cId}\n    address: ${v.address}\n    eco2:\n      name: "${name} eCO2"\n    tvoc:\n      name: "${name} TVOC"\n    update_interval: ${v.interval}`);
        case 'sgp30':
            return i2c(`sensor:\n  - platform: sgp30\n    i2c_id: ${i2cId}\n    eco2:\n      name: "${name} eCO2"\n    tvoc:\n      name: "${name} TVOC"\n    update_interval: ${v.interval}`);
        case 'sgp40':
            return i2c(`sensor:\n  - platform: sgp4x\n    i2c_id: ${i2cId}\n    voc_index:\n      name: "${name}"\n    update_interval: ${v.interval}`);
        case 'ens160':
            return i2c(`sensor:\n  - platform: ens160_i2c\n    i2c_id: ${i2cId}\n    address: ${v.address}\n    eco2:\n      name: "${name} eCO2"\n    tvoc:\n      name: "${name} TVOC"\n    aqi:\n      name: "${name} AQI"\n    update_interval: ${v.interval}`);
        case 'mhz19':
            return uart(9600, `sensor:\n  - platform: mhz19\n    uart_id: uart_${uid}\n    co2:\n      name: "${name}"\n    temperature:\n      name: "${name} 온도"\n    update_interval: ${v.interval}`);
        case 'pmsx003':
            return uart(9600, `sensor:\n  - platform: pmsx003\n    type: PMSX003\n    uart_id: uart_${uid}\n    pm_1_0:\n      name: "${name} PM1.0"\n    pm_2_5:\n      name: "${name} PM2.5"\n    pm_10_0:\n      name: "${name} PM10"`, '', v.rx);
        case 'sds011':
            return uart(9600, `sensor:\n  - platform: sds011\n    uart_id: uart_${uid}\n    pm_2_5:\n      name: "${name} PM2.5"\n    pm_10_0:\n      name: "${name} PM10"\n    update_interval: ${v.interval}`);
        case 'ina219':
            return i2c(`sensor:\n  - platform: ina219\n    i2c_id: ${i2cId}\n    address: ${v.address}\n    shunt_resistance: 0.1 ohm\n    current:\n      name: "${name} 전류"\n    power:\n      name: "${name} 전력"\n    bus_voltage:\n      name: "${name} 전압"\n    shunt_voltage:\n      name: "${name} Shunt 전압"\n    update_interval: ${v.interval}`);
        case 'ina226':
            return i2c(`sensor:\n  - platform: ina226\n    i2c_id: ${i2cId}\n    address: ${v.address}\n    shunt_resistance: ${v.shunt} ohm\n    max_current: ${v.maxCurrent}A\n    current:\n      name: "${name} 전류"\n    power:\n      name: "${name} 전력"\n    bus_voltage:\n      name: "${name} 전압"\n    shunt_voltage:\n      name: "${name} Shunt 전압"\n    update_interval: ${v.interval}`);
        case 'pzemac':
            return uart(9600, `modbus:\n  - id: modbus_${uid}\n    uart_id: uart_${uid}\n\nsensor:\n  - platform: pzemac\n    modbus_id: modbus_${uid}\n    address: ${v.address}\n    current:\n      name: "${name} 전류"\n    voltage:\n      name: "${name} 전압"\n    energy:\n      name: "${name} 전력량"\n    power:\n      name: "${name} 전력"\n    frequency:\n      name: "${name} 주파수"\n    power_factor:\n      name: "${name} 역률"\n    update_interval: ${v.interval}`);
        case 'pir':
            return `binary_sensor:\n  - platform: gpio\n    pin:\n      number: ${v.pin}\n      mode:\n        input: true\n    name: "${name}"\n    id: ${id}_motion\n    device_class: motion\n    filters:\n      - delayed_on: 100ms\n      - delayed_off: ${v.delayedOff}`;
        case 'gpio_binary':
            return `binary_sensor:\n  - platform: gpio\n    pin:\n      number: ${v.pin}\n      mode:\n        input: true\n        pullup: ${v.pullup}\n      inverted: ${v.inverted}\n    name: "${name}"\n    id: ${id}_input`;
        case 'reed':
            return `binary_sensor:\n  - platform: gpio\n    pin:\n      number: ${v.pin}\n      mode:\n        input: true\n        pullup: true\n      inverted: true\n    name: "${name}"\n    id: ${id}_contact\n    device_class: door`;
        case 'float':
            return `binary_sensor:\n  - platform: gpio\n    pin:\n      number: ${v.pin}\n      mode:\n        input: true\n        pullup: true\n      inverted: ${v.inverted}\n    name: "${name}"\n    id: ${id}_level`;
        case 'rain':
            return `binary_sensor:\n  - platform: gpio\n    pin:\n      number: ${v.pin}\n      mode:\n        input: true\n      inverted: ${v.inverted}\n    name: "${name}"\n    id: ${id}_rain\n    device_class: moisture`;
        case 'relay': {
            const channel = safeId(v.channel || 'relay1');
            return `mqtt:\n  on_json_message:\n    - topic: ${topic}/${channel}/set\n      then:\n        - lambda: |-\n            if (x["state"] == "on") {\n              id(${id}_relay).turn_on();\n            } else if (x["state"] == "off") {\n              id(${id}_relay).turn_off();\n            }\n\nswitch:\n  - platform: gpio\n    name: "${name}"\n    id: ${id}_relay\n    pin:\n      number: ${v.pin}\n      inverted: ${v.inverted}\n    restore_mode: ALWAYS_OFF\n    on_turn_on:\n      - mqtt.publish:\n          topic: ${topic}/${channel}/state\n          payload: '{"site_id":${siteId},"device_id":${deviceId},"state":"on"}'\n    on_turn_off:\n      - mqtt.publish:\n          topic: ${topic}/${channel}/state\n          payload: '{"site_id":${siteId},"device_id":${deviceId},"state":"off"}'`;
        }
        case 'gpio_switch':
            return `switch:\n  - platform: gpio\n    name: "${name}"\n    id: ${id}_switch\n    pin:\n      number: ${v.pin}\n      inverted: ${v.inverted}\n    restore_mode: ALWAYS_OFF`;
        case 'pwm_light':
            return `output:\n  - platform: ledc\n    pin: ${v.pin}\n    id: ${id}_pwm\n    frequency: ${v.frequency}\n\nlight:\n  - platform: monochromatic\n    name: "${name}"\n    output: ${id}_pwm`;
        case 'pwm_fan':
            return `output:\n  - platform: ledc\n    pin: ${v.pin}\n    id: ${id}_pwm\n    frequency: ${v.frequency}\n\nfan:\n  - platform: speed\n    name: "${name}"\n    output: ${id}_pwm`;
        case 'sen0601':
            return uart(9600, `modbus:
  - id: modbus_${uid}
    uart_id: uart_${uid}

modbus_controller:
  - id: controller_${uid}
    address: ${v.address}
    modbus_id: modbus_${uid}
    update_interval: ${v.interval}

sensor:
  - platform: modbus_controller
    modbus_controller_id: controller_${uid}
    name: "토양 수분 Moisture"
    id: ${id}_moisture
    register_type: holding
    address: 0x0000
    value_type: U_WORD
    unit_of_measurement: "%"
    device_class: moisture
    state_class: measurement
    accuracy_decimals: 1
    filters:
      - multiply: 0.1

  - platform: modbus_controller
    modbus_controller_id: controller_${uid}
    name: "토양 온도 Temperature"
    id: ${id}_temperature
    register_type: holding
    address: 0x0001
    value_type: S_WORD
    unit_of_measurement: "°C"
    device_class: temperature
    state_class: measurement
    accuracy_decimals: 1
    filters:
      - multiply: 0.1

  - platform: modbus_controller
    modbus_controller_id: controller_${uid}
    name: "토양 EC"
    id: ${id}_ec
    register_type: holding
    address: 0x0002
    value_type: U_WORD
    unit_of_measurement: "µS/cm"
    state_class: measurement
    accuracy_decimals: 0`, v.tx, v.rx);
        case 'modbus_sensor':
            return uart(v.baud, `modbus:\n  - id: modbus_${uid}\n    uart_id: uart_${uid}\n\nmodbus_controller:\n  - id: controller_${uid}\n    address: ${v.address}\n    modbus_id: modbus_${uid}\n    update_interval: ${v.interval}\n\nsensor:\n  - platform: modbus_controller\n    modbus_controller_id: controller_${uid}\n    name: "${name}"\n    id: ${id}_modbus\n    register_type: ${v.registerType}\n    address: ${v.register}\n    value_type: ${v.valueType}${v.unit ? `\n    unit_of_measurement: "${yamlQuote(v.unit)}"` : ''}${String(v.factor ?? '1.0') !== '1.0' ? `\n    filters:\n      - multiply: ${v.factor}` : ''}`);
        case 'custom_package':
            return String(v.yaml || '').trim();
        default:
            return '';
    }
};

const makeAutoBlock = (devices, siteId, deviceId, boardVariant = 'esp32') => {
    const encoded = encodeURIComponent(JSON.stringify(devices));
    if (!devices.length) {
        return `${AUTO_START}\n${AUTO_META} ${encoded}\n# 자동 장치 없음\n${AUTO_END}`;
    }

    const i2cTypes = new Set([
        'bme280', 'bmp280', 'sht3xd', 'sht4x', 'aht10', 'hdc1080',
        'bme680', 'bh1750', 'veml7700', 'tsl2561', 'ccs811', 'sgp30',
        'sgp40', 'ens160', 'ina219', 'ina226',
    ]);

    const i2cBuses = [];
    const seenI2c = new Set();

    devices.forEach(device => {
        if (!i2cTypes.has(device.type)) return;
        const values = device.values || {};
        const sda = values.sda || 'GPIO21';
        const scl = values.scl || 'GPIO22';
        const busId = `i2c_${safeId(`${sda}_${scl}`)}`;
        if (seenI2c.has(busId)) return;
        seenI2c.add(busId);
        i2cBuses.push({ busId, sda, scl });
    });

    const packageParts = [];

    if (i2cBuses.length) {
        const busYaml = [
            'i2c:',
            ...i2cBuses.flatMap(bus => [
                `  - id: ${bus.busId}`,
                `    sda: ${bus.sda}`,
                `    scl: ${bus.scl}`,
                '    scan: true',
            ]),
        ].join('\n');

        packageParts.push(
            `  kg_auto_i2c_buses:\n${indentYaml(busYaml, 4)}`
        );
    }

    devices.forEach((device, index) => {
        const body = makeDevicePackage(device, siteId, deviceId, boardVariant);
        packageParts.push(
            `  kg_auto_${index + 1}_${safeId(device.key)}:\n${indentYaml(body, 4)}`
        );
    });

    return `${AUTO_START}\n${AUTO_META} ${encoded}\npackages:\n${packageParts.join('\n\n')}\n${AUTO_END}`;
};

const replaceAutoBlock = (yamlText, devices, siteId, deviceId) => {
    const boardVariant = /^\s*variant:\s*esp32s3\s*$/m.test(String(yamlText || '')) ? 'esp32s3' : 'esp32';
    const block = makeAutoBlock(devices, siteId, deviceId, boardVariant);
    const source = String(yamlText || '').trimEnd();
    const start = source.indexOf(AUTO_START);
    const end = source.indexOf(AUTO_END);

    if (start >= 0 && end >= start) {
        return source.slice(0, start)
            + block
            + source.slice(end + AUTO_END.length);
    }

    return source + '\n\n' + block + '\n';
};

const parseAutoDevices = (yamlText) => {
    const line = String(yamlText || '')
        .split('\n')
        .find(item => item.startsWith(AUTO_META));

    if (!line) return [];

    try {
        const encoded = line.slice(AUTO_META.length).trim();
        const parsed = JSON.parse(decodeURIComponent(encoded));
        return Array.isArray(parsed) ? parsed : [];
    } catch (_) {
        return [];
    }
};


// 양액기 장치 판별: 장치명/서버 장치유형 + 기존 저장 ID를 함께 사용합니다.
// 일반 장치를 잘못 양액기로 전환하지 않도록 명시적 표시만 인정합니다.
const isNutrientDevice = (device, siteId) => {
    const id = Number(device?.device_id || 0);
    const type = String(device?.device_type || device?.type || device?.builder_type || '').toLowerCase();
    const name = String(device?.device_name || device?.name || '').toLowerCase();
    const lastId = Number(window.localStorage.getItem(`konggomul_nutrient_device_${siteId}`) || 0);
    let ids = [];
    try { ids = JSON.parse(window.localStorage.getItem(`konggomul_nutrient_devices_${siteId}`) || '[]'); } catch (_) {}
    return type === 'nutrient' || type === 'nutrient_builder' || /양액|nutrient/.test(name)
        || id === lastId || (Array.isArray(ids) && ids.some(x => Number(x) === id));
};

function OriginalEsp32Builder() {

    const [searchParams] =
        useSearchParams();

    const navigate =
        useNavigate();

    const siteId =
        Number(
            searchParams.get('site_id') || 0
        );

    const queryDeviceId =
        Number(
            searchParams.get('device_id') || 0
        );


    // ==========================================================
    // State
    // ==========================================================

    const [devices, setDevices] =
        useState([]);

    const [deviceId, setDeviceId] =
        useState(queryDeviceId);

    const [yaml, setYaml] =
        useState('');

    const [wifiSsid, setWifiSsid] =
        useState('');

    const [wifiPassword, setWifiPassword] =
        useState('');

    const [esp32Ip, setEsp32Ip] =
        useState('');

    const [loading, setLoading] =
        useState(true);

    const [working, setWorking] =
        useState(false);

    const [otaWorking, setOtaWorking] =
        useState(false);

    const [message, setMessage] =
        useState('');

    const [output, setOutput] =
        useState('');

    const [buildSuccess, setBuildSuccess] =
        useState(false);

    const [firmwareUrl, setFirmwareUrl] =
        useState('');


    const [factoryFirmwareUrl, setFactoryFirmwareUrl] =
        useState('');

    const [otaSuccess, setOtaSuccess] =
        useState(false);


    // ==========================================================
    // 선택형 자동 장치 구성
    // ==========================================================

    const [devicePanelOpen, setDevicePanelOpen] =
        useState(true);

    const [autoDevices, setAutoDevices] =
        useState([]);

    const [selectedDeviceType, setSelectedDeviceType] =
        useState(DEVICE_CATALOG[0].type);

    const [deviceForm, setDeviceForm] =
        useState(() => {
            const first = DEVICE_CATALOG[0];
            return Object.fromEntries(
                first.fields.map(field => [field.key, field.default ?? ''])
            );
        });


    // ==========================================================
    // ESP32 장치 목록 + YAML 불러오기 + ESP32 IP
    // ==========================================================

    useEffect(() => {

        if (!siteId) {

            setLoading(false);

            return;
        }

        let cancelled = false;


        const load = async () => {

            try {

                setLoading(true);

                setMessage(
                    'ESP32 장치 정보 불러오는 중...'
                );


                // ------------------------------------------------
                // 농장에 등록된 ESP32 장치 목록
                // ------------------------------------------------

                const devicesResponse =
                    await fetch(
                        `/api/seller/smartfarm/esp32/devices?site_id=${siteId}`,
                        {
                            credentials: 'include',
                            cache: 'no-store',
                        }
                    );


                const devicesData =
                    await devicesResponse.json();


                if (
                    !devicesResponse.ok ||
                    !devicesData.ok
                ) {

                    throw new Error(
                        devicesData.error ||
                        'ESP32 장치 목록을 불러오지 못했습니다.'
                    );
                }


                const loadedDevices =
                    Array.isArray(
                        devicesData.devices
                    )
                        ? devicesData.devices
                        : [];


                if (!loadedDevices.length) {

                    throw new Error(
                        '등록된 ESP32 장치가 없습니다.'
                    );
                }


                let resolvedDeviceId =
                    Number(
                        queryDeviceId || 0
                    );


                const requestedExists =
                    loadedDevices.some(
                        device =>
                            Number(
                                device.device_id
                            )
                            === resolvedDeviceId
                    );


                if (!requestedExists) {

                    resolvedDeviceId =
                        Number(
                            loadedDevices[0]
                                .device_id || 0
                        );
                }


                if (!cancelled) {

                    setDevices(
                        loadedDevices
                    );

                    setDeviceId(
                        resolvedDeviceId
                    );
                }


                // 기존 양액기 장치도 항상 양액기 전용 빌더에서 편집합니다.
                const resolvedDevice = loadedDevices.find(d => Number(d.device_id) === resolvedDeviceId);
                if (resolvedDeviceId > 0 && isNutrientDevice(resolvedDevice, siteId)) {
                    navigate(`/seller/smartfarm/esp32/nutrient-builder?site_id=${siteId}&device_id=${resolvedDeviceId}`, { replace: true });
                    return;
                }

                // URL에 device_id가 없거나 잘못된 값이면
                // 실제 선택된 장치 ID로 정규화한다.
                if (
                    resolvedDeviceId > 0 &&
                    resolvedDeviceId !==
                        queryDeviceId
                ) {

                    navigate(
                        `/seller/smartfarm/esp32/builder`
                        + `?site_id=${siteId}`
                        + `&device_id=${resolvedDeviceId}`,
                        {
                            replace: true,
                        }
                    );

                    return;
                }


                // ------------------------------------------------
                // YAML
                // ------------------------------------------------

                const response =
                    await fetch(
                        `${API_BASE}/config`
                        + `?site_id=${siteId}`
                        + `&device_id=${resolvedDeviceId}`,
                        {
                            credentials: 'include',
                            cache: 'no-store',
                        }
                    );


                const data =
                    await response.json();


                if (
                    !response.ok ||
                    !data.ok
                ) {

                    throw new Error(
                        data.error ||
                        'YAML을 불러오지 못했습니다.'
                    );
                }


                if (!cancelled) {

                    setYaml(
                        data.yaml || ''
                    );

                    setAutoDevices(
                        parseAutoDevices(
                            data.yaml || ''
                        )
                    );

                    setMessage(
                        'YAML 불러오기 완료'
                    );
                }


                // ------------------------------------------------
                // ESP32 IP
                //
                // 아직 MQTT 실시간 상태는 다음 단계에서
                // device_id 기준으로 완전히 분리한다.
                // 요청에는 미리 device_id를 전달한다.
                // ------------------------------------------------

                try {

                    const sensorResponse =
                        await fetch(
                            `/api/seller/smartfarm/esp32/sensors`
                            + `?site_id=${siteId}`
                            + `&device_id=${resolvedDeviceId}`,
                            {
                                credentials: 'include',
                                cache: 'no-store',
                            }
                        );


                    const sensorData =
                        await sensorResponse.json();


                    if (
                        sensorResponse.ok &&
                        sensorData.ok &&
                        !cancelled
                    ) {

                        setEsp32Ip(
                            sensorData.esp32_ip || ''
                        );
                    }

                } catch (_) {

                    // IP 조회 실패는 YAML 로딩 실패로 처리하지 않는다.

                }

            } catch (error) {

                if (!cancelled) {

                    setMessage(
                        error.message ||
                        '설정을 불러오지 못했습니다.'
                    );
                }

            } finally {

                if (!cancelled) {

                    setLoading(false);
                }
            }
        };


        load();


        return () => {

            cancelled = true;
        };

    }, [
        siteId,
        queryDeviceId,
        navigate,
    ]);


    // ==========================================================
    // ESP Web Tools
    //
    // 최초 USB 설치용 Factory 펌웨어를 브라우저에서
    // ESP32에 직접 업로드하기 위한 install button 모듈.
    // 기존 Builder / Download / OTA 로직과는 분리한다.
    // ==========================================================

    useEffect(() => {

        const scriptId =
            'esp-web-tools-install-button';


        if (
            document.getElementById(
                scriptId
            )
        ) {

            return;
        }


        const script =
            document.createElement(
                'script'
            );


        script.id = scriptId;

        script.type = 'module';

        script.src =
            'https://unpkg.com/esp-web-tools@10/dist/web/install-button.js?module';


        document.head.appendChild(
            script
        );

    }, []);


    // ==========================================================
    // Save
    // ==========================================================

    const saveYaml = async () => {

        if (!siteId) {

            setMessage(
                '스마트팜 ID가 없습니다.'
            );

            return;
        }

        if (!deviceId) {

            setMessage(
                'ESP32 장치를 선택하세요.'
            );

            return;
        }


        setWorking(true);

        setMessage(
            '저장 중...'
        );


        try {

            const response =
                await fetch(
                    `${API_BASE}/save`,
                    {
                        method: 'POST',

                        headers: {
                            'Content-Type':
                                'application/json',
                        },

                        credentials: 'include',

                        body: JSON.stringify({
                            site_id: siteId,
                            device_id: deviceId,
                            yaml,
                        }),
                    }
                );


            const data =
                await response.json();


            if (
                !response.ok ||
                !data.ok
            ) {

                throw new Error(
                    data.error ||
                    '저장 실패'
                );
            }


            setMessage(
                'YAML이 저장되었습니다.'
            );

        } catch (error) {

            setMessage(
                error.message ||
                '저장 실패'
            );

        } finally {

            setWorking(false);
        }
    };


    // ==========================================================
    // Validate
    // ==========================================================

    const validateYaml = async () => {

        if (!wifiSsid.trim()) {

            setMessage(
                'Wi-Fi SSID를 입력하세요.'
            );

            return;
        }


        if (!wifiPassword) {

            setMessage(
                'Wi-Fi 비밀번호를 입력하세요.'
            );

            return;
        }


        setWorking(true);

        setOutput('');

        setBuildSuccess(false);

        setFirmwareUrl('');

        setFactoryFirmwareUrl('');

        setOtaSuccess(false);


        setMessage(
            'ESPHome YAML 검증 중...'
        );


        try {

            const response =
                await fetch(
                    `${API_BASE}/validate`,
                    {
                        method: 'POST',

                        headers: {
                            'Content-Type':
                                'application/json',
                        },

                        credentials: 'include',

                        body: JSON.stringify({
                            site_id: siteId,
                            device_id: deviceId,
                            yaml,

                            wifi_ssid:
                                wifiSsid.trim(),

                            wifi_password:
                                wifiPassword,
                        }),
                    }
                );


            const data =
                await response.json();


            setOutput(
                data.output ||
                data.error ||
                ''
            );


            if (
                !response.ok ||
                !data.ok
            ) {

                throw new Error(
                    data.message ||
                    data.error ||
                    'YAML 검증 실패'
                );
            }


            setMessage(
                '✓ YAML 검증 성공'
            );

        } catch (error) {

            setMessage(
                `✕ ${
                    error.message ||
                    'YAML 검증 실패'
                }`
            );

        } finally {

            setWorking(false);
        }
    };


    // ==========================================================
    // Build
    // ==========================================================

    const buildFirmware = async () => {

        if (!wifiSsid.trim()) {

            setMessage(
                'Wi-Fi SSID를 입력하세요.'
            );

            return;
        }


        if (!wifiPassword) {

            setMessage(
                'Wi-Fi 비밀번호를 입력하세요.'
            );

            return;
        }


        setWorking(true);

        setOutput('');

        setBuildSuccess(false);

        setFirmwareUrl('');

        setFactoryFirmwareUrl('');

        setOtaSuccess(false);


        setMessage(
            'ESPHome 펌웨어 컴파일 중...'
        );


        try {

            const response =
                await fetch(
                    `${API_BASE}/build`,
                    {
                        method: 'POST',

                        headers: {
                            'Content-Type':
                                'application/json',
                        },

                        credentials: 'include',

                        body: JSON.stringify({
                            site_id: siteId,
                            device_id: deviceId,
                            yaml,

                            wifi_ssid:
                                wifiSsid.trim(),

                            wifi_password:
                                wifiPassword,
                        }),
                    }
                );


            const data =
                await response.json();


            setOutput(
                data.output ||
                data.error ||
                ''
            );


            if (
                !response.ok ||
                !data.ok
            ) {

                throw new Error(
                    data.message ||
                    data.error ||
                    '펌웨어 빌드 실패'
                );
            }


            setBuildSuccess(true);


            setFirmwareUrl(
                data.firmware ||
                ''
            );


            setFactoryFirmwareUrl(
                data.factory_firmware ||
                ''
            );


            setMessage(
                '✓ 펌웨어 빌드 완료'
            );

        } catch (error) {

            setMessage(
                `✕ ${
                    error.message ||
                    '펌웨어 빌드 실패'
                }`
            );

        } finally {

            setWorking(false);
        }
    };


    // ==========================================================
    // OTA - HTTP Pull 방식
    //
    // 서버가 농장 ESP32의 사설 IP로 접속하지 않는다.
    // Flask가 MQTT로 OTA 시작 명령만 보내고,
    // ESP32가 HTTPS로 최신 firmware.ota.bin을 직접 받는다.
    // ==========================================================

    const uploadOta = async () => {

        if (!siteId) {

            setMessage(
                '스마트팜 ID가 없습니다.'
            );

            return;
        }

        if (!deviceId) {

            setMessage(
                'ESP32 장치를 선택하세요.'
            );

            return;
        }


        if (!buildSuccess) {

            setMessage(
                '먼저 펌웨어를 Build하세요.'
            );

            return;
        }


        const confirmed =
            window.confirm(
                '새 펌웨어로 원격 OTA 업데이트하시겠습니까?\n\n'
                + '농장 내부 IP 연결은 사용하지 않습니다.\n'
                + 'ESP32가 HTTPS로 펌웨어를 직접 다운로드한 뒤 재부팅됩니다.'
            );


        if (!confirmed) {

            return;
        }


        setOtaWorking(true);

        setOtaSuccess(false);

        setOutput('');

        setMessage(
            'ESP32에 OTA 시작 명령 전송 중...'
        );


        try {

            const response =
                await fetch(
                    `${API_BASE}/ota`,
                    {
                        method: 'POST',

                        headers: {
                            'Content-Type':
                                'application/json',
                        },

                        credentials: 'include',

                        body: JSON.stringify({
                            site_id: siteId,
                            device_id: deviceId,
                        }),
                    }
                );


            const data =
                await response.json();


            if (
                !response.ok ||
                !data.ok
            ) {

                throw new Error(
                    data.error ||
                    data.message ||
                    'OTA 시작 명령 전송 실패'
                );
            }


            setMessage(
                'OTA 명령 전송 완료 · ESP32 다운로드 대기 중...'
            );


            await pollOtaStatus();

        } catch (error) {

            setOtaSuccess(false);

            setMessage(
                `✕ ${
                    error.message ||
                    'OTA 업데이트 실패'
                }`
            );

        } finally {

            setOtaWorking(false);
        }
    };


    // ==========================================================
    // OTA 상태 polling
    //
    // Flask status.json 상태:
    // idle -> command_sending -> command_sent
    //      -> downloading -> downloaded
    //      -> failed
    //
    // downloaded는 ESP32가 firmware.ota.bin 전체를 HTTP로
    // 받아간 상태다. 이후 장치가 flash/reboot를 수행한다.
    // ==========================================================

    const pollOtaStatus = async () => {

        const maxAttempts = 180;

        for (
            let attempt = 0;
            attempt < maxAttempts;
            attempt += 1
        ) {

            const response =
                await fetch(
                    `${API_BASE}/ota-status?site_id=${siteId}&device_id=${deviceId}`,
                    {
                        credentials: 'include',
                        cache: 'no-store',
                    }
                );


            const data =
                await response.json();


            if (
                !response.ok ||
                !data.ok
            ) {

                throw new Error(
                    data.error ||
                    'OTA 상태 조회 실패'
                );
            }


            const status =
                data.status ||
                'idle';


            if (status === 'failed') {

                throw new Error(
                    data.error ||
                    'ESP32 OTA 업데이트 실패'
                );
            }


            if (status === 'downloading') {

                setMessage(
                    'ESP32가 새 펌웨어를 HTTPS로 다운로드 중...'
                );
            }


            if (status === 'downloaded') {

                setOtaSuccess(true);

                setMessage(
                    '✓ 펌웨어 전송 완료 · ESP32 플래시/재부팅 진행 중'
                );

                return;
            }


            if (
                status === 'command_sending' ||
                status === 'command_sent'
            ) {

                setMessage(
                    'OTA 명령 전송 완료 · ESP32 응답 대기 중...'
                );
            }


            await new Promise(
                resolve =>
                    setTimeout(
                        resolve,
                        2000
                    )
            );
        }


        throw new Error(
            'ESP32가 OTA 펌웨어 다운로드를 시작하지 않았습니다.'
        );
    };


    // ==========================================================
    // Firmware Download
    // ==========================================================

    const downloadFirmware = async () => {

        if (!siteId) {

            return;
        }


        if (!firmwareUrl) {

            setMessage(
                '다운로드할 펌웨어가 없습니다.'
            );

            return;
        }


        try {

            setMessage(
                '펌웨어 다운로드 준비 중...'
            );


            const response =
                await fetch(
                    firmwareUrl,
                    {
                        credentials: 'include',
                    }
                );


            if (!response.ok) {

                let data = null;


                try {

                    data =
                        await response.json();

                } catch (_) {

                    // ignore
                }


                throw new Error(
                    data?.error ||
                    '펌웨어 다운로드 실패'
                );
            }


            const blob =
                await response.blob();


            const url =
                window.URL.createObjectURL(
                    blob
                );


            const link =
                document.createElement(
                    'a'
                );


            link.href = url;


            link.download =
                `site_${siteId}_device_${deviceId}_firmware.ota.bin`;


            document.body.appendChild(
                link
            );


            link.click();


            link.remove();


            window.URL.revokeObjectURL(
                url
            );


            setMessage(
                '펌웨어 다운로드 완료'
            );

        } catch (error) {

            setMessage(
                `✕ ${
                    error.message ||
                    '펌웨어 다운로드 실패'
                }`
            );
        }
    };



    // ==========================================================
    // Factory Firmware Download
    //
    // 최초 USB 설치용 firmware.factory.bin
    // ==========================================================

    const downloadFactoryFirmware = async () => {

        if (!siteId) {

            return;
        }


        if (!factoryFirmwareUrl) {

            setMessage(
                '다운로드할 Factory 펌웨어가 없습니다.'
            );

            return;
        }


        try {

            setMessage(
                'Factory 펌웨어 다운로드 준비 중...'
            );


            const response =
                await fetch(
                    factoryFirmwareUrl,
                    {
                        credentials: 'include',
                    }
                );


            if (!response.ok) {

                let data = null;


                try {

                    data =
                        await response.json();

                } catch (_) {

                    // ignore
                }


                throw new Error(
                    data?.error ||
                    'Factory 펌웨어 다운로드 실패'
                );
            }


            const blob =
                await response.blob();


            const url =
                window.URL.createObjectURL(
                    blob
                );


            const link =
                document.createElement(
                    'a'
                );


            link.href = url;


            link.download =
                `site_${siteId}_device_${deviceId}_firmware.factory.bin`;


            document.body.appendChild(
                link
            );


            link.click();


            link.remove();


            window.URL.revokeObjectURL(
                url
            );


            setMessage(
                'Factory 펌웨어 다운로드 완료'
            );

        } catch (error) {

            setMessage(
                `✕ ${
                    error.message ||
                    'Factory 펌웨어 다운로드 실패'
                }`
            );
        }
    };


    // ==========================================================
    // 펌웨어 초기화
    //
    // 선택한 site_id + device_id의 최초 Bootstrap YAML을
    // 에디터에 다시 불러온다.
    //
    // 중요:
    // 이 버튼만 눌러서는 DB에 저장된 기존 YAML을 변경하지 않는다.
    // 이후 Validate -> Build -> Factory Download 순서로
    // USB 복구용 firmware.factory.bin을 만들 수 있다.
    // ==========================================================

    const resetFirmwareToBootstrap = async () => {

        if (!siteId) {

            setMessage(
                '스마트팜 ID가 없습니다.'
            );

            return;
        }

        if (!deviceId) {

            setMessage(
                'ESP32 장치를 선택하세요.'
            );

            return;
        }


        const confirmed =
            window.confirm(
                '선택한 ESP32의 초기 Bootstrap 펌웨어를 불러오시겠습니까?\n\n'
                + '현재 DB에 저장된 YAML은 이 단계에서 변경되지 않습니다.\n'
                + '에디터 내용만 초기 펌웨어 YAML로 교체됩니다.\n\n'
                + '이후 Validate → Build → Factory Download 후 '
                + 'USB로 ESP32에 업로드하면 복구할 수 있습니다.'
            );


        if (!confirmed) {

            return;
        }


        try {

            setWorking(true);

            setOutput('');

            setBuildSuccess(false);

            setFirmwareUrl('');

            setFactoryFirmwareUrl('');

            setOtaSuccess(false);

            setMessage(
                '초기 Bootstrap 펌웨어 불러오는 중...'
            );


            const response =
                await fetch(
                    `${API_BASE}/bootstrap`
                    + `?site_id=${siteId}`
                    + `&device_id=${deviceId}`,
                    {
                        credentials: 'include',
                        cache: 'no-store',
                    }
                );


            const data =
                await response.json();


            if (
                !response.ok ||
                !data.ok
            ) {

                throw new Error(
                    data.error ||
                    '초기 펌웨어를 불러오지 못했습니다.'
                );
            }


            setYaml(
                data.yaml || ''
            );

            setAutoDevices(
                parseAutoDevices(
                    data.yaml || ''
                )
            );


            setMessage(
                '✓ 초기 Bootstrap YAML을 불러왔습니다. '
                + 'Validate → Build → Factory Download 순서로 진행하세요.'
            );

        } catch (error) {

            setMessage(
                `✕ ${
                    error.message ||
                    '초기 펌웨어 불러오기 실패'
                }`
            );

        } finally {

            setWorking(false);
        }
    };


    // ==========================================================
    // 운영 기본 설정
    //
    // Factory Bootstrap 설치가 끝난 뒤 센서 / 스위치를 추가하기 위한
    // 공통 운영 YAML을 에디터에 불러온다.
    //
    // 이 단계에서는 DB에 저장하지 않고 에디터 내용만 교체한다.
    // 실제 장치 설정을 추가한 뒤 Save -> Build -> OTA 한다.
    // ==========================================================

    const loadOperatingBase = async () => {

        if (!siteId) {

            setMessage(
                '스마트팜 ID가 없습니다.'
            );

            return;
        }

        if (!deviceId) {

            setMessage(
                'ESP32 장치를 선택하세요.'
            );

            return;
        }


        const confirmed =
            window.confirm(
                '운영 기본 설정을 에디터에 불러오시겠습니까?\n\n'
                + '현재 DB에 저장된 YAML은 변경되지 않습니다.\n'
                + '에디터 내용만 운영 기본 YAML로 교체됩니다.\n\n'
                + '이후 센서 / PIR / 릴레이 등 실제 장치 설정을 추가한 뒤\n'
                + 'Save → Build → OTA 순서로 진행하세요.'
            );


        if (!confirmed) {

            return;
        }


        try {

            setWorking(true);

            setOutput('');

            setBuildSuccess(false);

            setFirmwareUrl('');

            setFactoryFirmwareUrl('');

            setOtaSuccess(false);

            setMessage(
                '운영 기본 설정 불러오는 중...'
            );


            const response =
                await fetch(
                    `${API_BASE}/operating-base`
                    + `?site_id=${siteId}`
                    + `&device_id=${deviceId}`,
                    {
                        credentials: 'include',
                        cache: 'no-store',
                    }
                );


            const data =
                await response.json();


            if (
                !response.ok ||
                !data.ok
            ) {

                throw new Error(
                    data.error ||
                    '운영 기본 설정을 불러오지 못했습니다.'
                );
            }


            setYaml(
                data.yaml || ''
            );


            setMessage(
                '✓ 운영 기본 YAML을 불러왔습니다. '
                + '센서 / 스위치 설정 추가 → Save → Build → OTA 순서로 진행하세요.'
            );

        } catch (error) {

            setMessage(
                `✕ ${
                    error.message ||
                    '운영 기본 설정 불러오기 실패'
                }`
            );

        } finally {

            setWorking(false);
        }
    };



    // ==========================================================
    // 자동 장치 추가 / 삭제
    // ==========================================================

    const selectedDeviceDefinition =
        DEVICE_CATALOG.find(
            item => item.type === selectedDeviceType
        ) || DEVICE_CATALOG[0];

    const changeSelectedDeviceType = (type) => {
        const definition =
            DEVICE_CATALOG.find(
                item => item.type === type
            ) || DEVICE_CATALOG[0];

        setSelectedDeviceType(type);
        const defaults = Object.fromEntries(
            definition.fields.map(field => [field.key, field.default ?? ''])
        );
        if (/^\s*variant:\s*esp32s3\s*$/m.test(yaml) &&
            (type === 'sen0601' || type === 'modbus_sensor')) {
            defaults.tx = 'GPIO17';
            defaults.rx = 'GPIO18';
        }
        setDeviceForm(defaults);
    };

    const addAutoDevice = () => {
        if (!yaml.includes('mqtt:')) {
            setMessage(
                '먼저 운영 기본 설정을 불러온 뒤 장치를 추가하세요.'
            );
            return;
        }

        const missing =
            selectedDeviceDefinition.fields.find(
                field =>
                    field.key !== 'unit' &&
                    String(deviceForm[field.key] ?? '').trim() === ''
            );

        if (missing) {
            setMessage(`${missing.label} 값을 입력하세요.`);
            return;
        }

        const newDevice = {
            key: `${selectedDeviceType}_${Date.now()}`,
            type: selectedDeviceType,
            label: selectedDeviceDefinition.label,
            values: { ...deviceForm },
        };

        const next = [
            ...autoDevices,
            newDevice,
        ];

        setAutoDevices(next);
        setYaml(
            replaceAutoBlock(
                yaml,
                next,
                siteId,
                deviceId
            )
        );
        setBuildSuccess(false);
        setFirmwareUrl('');
        setFactoryFirmwareUrl('');
        setOtaSuccess(false);
        setMessage(
            `✓ ${selectedDeviceDefinition.label} 자동 설정을 추가했습니다. Validate로 확인하세요.`
        );
    };

    const removeAutoDevice = (key) => {
        const next = autoDevices.filter(
            device => device.key !== key
        );

        setAutoDevices(next);
        setYaml(
            replaceAutoBlock(
                yaml,
                next,
                siteId,
                deviceId
            )
        );
        setBuildSuccess(false);
        setFirmwareUrl('');
        setFactoryFirmwareUrl('');
        setOtaSuccess(false);
        setMessage('자동 장치 설정을 삭제했습니다.');
    };

    // ==========================================================
    // ESP32 장치 추가
    // ==========================================================

    const addDevice = async (boardType = 'esp32') => {

        if (!siteId) {

            setMessage(
                '스마트팜 ID가 없습니다.'
            );

            return;
        }


        if (
            working ||
            otaWorking ||
            loading
        ) {

            return;
        }


        const inputName =
            window.prompt(
                `추가할 ${boardType === 'esp32s3' ? 'ESP32-S3' : 'ESP32'} 이름을 입력하세요.\n`
                + '비워두면 자동으로 ESP32 #N 이름이 생성됩니다.',
                ''
            );


        if (inputName === null) {

            return;
        }


        try {

            setWorking(true);

            setMessage(
                '새 ESP32 장치 등록 중...'
            );


            const response =
                await fetch(
                    '/api/seller/smartfarm/esp32/devices',
                    {
                        method: 'POST',

                        headers: {
                            'Content-Type':
                                'application/json',
                        },

                        credentials: 'include',

                        body: JSON.stringify({
                            site_id: siteId,
                            board_type: boardType,
                            device_name:
                                inputName.trim(),
                        }),
                    }
                );


            const data =
                await response.json();


            if (
                !response.ok ||
                !data.ok
            ) {

                throw new Error(
                    data.error ||
                    'ESP32 장치 추가 실패'
                );
            }


            const newDeviceId =
                Number(
                    data.device_id || 0
                );


            if (newDeviceId <= 0) {

                throw new Error(
                    '새 ESP32 device_id를 받지 못했습니다.'
                );
            }


            navigate(
                `/seller/smartfarm/esp32/builder`
                + `?site_id=${siteId}`
                + `&device_id=${newDeviceId}`
            );

        } catch (error) {

            setMessage(
                `✕ ${
                    error.message ||
                    'ESP32 장치 추가 실패'
                }`
            );

        } finally {

            setWorking(false);
        }
    };


    // ==========================================================
    // Factory Web Install Manifest URL
    // ==========================================================

    const factoryManifestUrl =
        factoryFirmwareUrl
            ? factoryFirmwareUrl.replace(
                '/esp32/builder/factory/',
                '/esp32/builder/factory-manifest/'
            )
            : '';


    // ==========================================================
    // site_id 없음
    // ==========================================================

    if (!siteId) {

        return (
            <div
                style={{
                    padding: 30,
                }}
            >
                스마트팜 ID가 없습니다.
            </div>
        );
    }


    // ==========================================================
    // Render
    // ==========================================================

    return (
        <div
            style={{
                height: '100vh',
                display: 'flex',
                flexDirection: 'column',
                background: '#111',
            }}
        >

            {/* ==================================================
                Header
            ================================================== */}

            <div
                style={{
                    minHeight: 52,
                    display: 'flex',
                    alignItems: 'center',
                    padding: '0 14px',
                    background: '#08a4c7',
                    color: '#fff',
                    gap: 12,
                    flexShrink: 0,
                }}
            >

                <button
                    type="button"
                    onClick={() =>
                        navigate(
                            `/seller/smartfarm/esp32?site_id=${siteId}&device_id=${deviceId}`
                        )
                    }
                    style={{
                        border: 'none',
                        background: 'transparent',
                        color: '#fff',
                        fontSize: 22,
                        cursor: 'pointer',
                    }}
                >
                    ←
                </button>


                <div
                    style={{
                        fontSize: 15,
                        fontWeight: 600,
                    }}
                >
                    ESPHome Device Builder
                </div>


                {/* ----------------------------------------------
                    ESP32 상태
                ---------------------------------------------- */}

                <div
                    style={{
                        marginLeft: 'auto',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        fontSize: 12,
                    }}
                >

                    <select
                        value={deviceId || ''}
                        onChange={(e) => {

                            const nextDeviceId =
                                Number(
                                    e.target.value || 0
                                );

                            if (
                                nextDeviceId <= 0 ||
                                nextDeviceId === deviceId
                            ) {

                                return;
                            }

                            // 양액기 장치는 전용 빌더에서만 편집합니다.
                            const selectedDevice = devices.find(d => Number(d.device_id) === nextDeviceId);
                            navigate(
                                isNutrientDevice(selectedDevice, siteId)
                                    ? `/seller/smartfarm/esp32/nutrient-builder?site_id=${siteId}&device_id=${nextDeviceId}`
                                    : `/seller/smartfarm/esp32/builder?site_id=${siteId}&device_id=${nextDeviceId}`
                            );
                        }}
                        disabled={
                            loading ||
                            working ||
                            otaWorking
                        }
                        style={{
                            height: 30,
                            border:
                                '1px solid rgba(255,255,255,0.45)',
                            borderRadius: 5,
                            background: '#087f9b',
                            color: '#fff',
                            padding: '0 6px',
                            outline: 'none',
                        }}
                    >
                        {devices.map(
                            device => (
                                <option
                                    key={
                                        device.device_id
                                    }
                                    value={
                                        device.device_id
                                    }
                                >
                                    {device.device_name
                                        || `ESP32 #${device.device_id}`}
                                </option>
                            )
                        )}
                    </select>

                    <button
                        type="button"
                        onClick={() => addDevice('esp32')}
                        disabled={
                            loading ||
                            working ||
                            otaWorking
                        }
                        title="이 농장에 ESP32 추가"
                        style={{
                            height: 30,
                            border:
                                '1px solid rgba(255,255,255,0.55)',
                            borderRadius: 5,
                            background:
                                'rgba(255,255,255,0.14)',
                            color: '#fff',
                            padding: '0 9px',
                            fontWeight: 700,
                            cursor:
                                loading ||
                                working ||
                                otaWorking
                                    ? 'default'
                                    : 'pointer',
                        }}
                    >
                        + ESP32
                    </button>

                    <button
                        type="button"
                        onClick={() => addDevice('esp32s3')}
                        disabled={loading || working || otaWorking}
                        title="Waveshare ESP32-S3-Relay-1CH 추가"
                        style={{height:30,border:'1px solid rgba(255,255,255,0.55)',borderRadius:5,background:'#176d9d',color:'#fff',padding:'0 9px',fontWeight:700,cursor:loading || working || otaWorking ? 'default' : 'pointer',whiteSpace:'nowrap'}}
                    >
                        + ESP32-S3
                    </button>

                    <button
                        type="button"
                        onClick={() => window.open(
                            `/seller/smartfarm/esp32/nutrient-builder?site_id=${siteId}`,
                            '_blank', 'noopener,noreferrer'
                        )}
                        title="별도 창에서 독립 양액기 ESP32 구성"
                        style={{height:30,border:'1px solid rgba(255,255,255,0.55)',borderRadius:5,
                            background:'#17644d',color:'#fff',padding:'0 10px',fontWeight:700,cursor:'pointer',whiteSpace:'nowrap'}}
                    >
                        + ESP32 양액기
                    </button>

                    <span
                        style={{
                            opacity: 0.75,
                        }}
                    >
                        ESP32
                    </span>


                    <span
                        style={{
                            fontFamily:
                                'monospace',
                            fontWeight: 600,
                        }}
                    >
                        {esp32Ip ||
                            'IP 없음'}
                    </span>

                </div>

            </div>


            {/* ==================================================
                Wi-Fi 설정
            ================================================== */}

            <div
                style={{
                    background: '#181c20',
                    padding: '10px 14px',
                    display: 'flex',
                    gap: 10,
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    flexShrink: 0,
                    borderBottom:
                        '1px solid #30363d',
                }}
            >

                <div
                    style={{
                        color: '#aaa',
                        fontSize: 12,
                        fontWeight: 600,
                    }}
                >
                    현장 Wi-Fi
                </div>


                <input
                    type="text"
                    value={wifiSsid}
                    onChange={(e) =>
                        setWifiSsid(
                            e.target.value
                        )
                    }
                    placeholder="Wi-Fi SSID"
                    disabled={
                        working ||
                        otaWorking
                    }
                    autoComplete="off"
                    style={{
                        width: 180,
                        height: 34,
                        boxSizing: 'border-box',
                        border:
                            '1px solid #444',
                        borderRadius: 6,
                        background: '#0f1114',
                        color: '#fff',
                        padding: '0 10px',
                        outline: 'none',
                    }}
                />


                <input
                    type="password"
                    value={wifiPassword}
                    onChange={(e) =>
                        setWifiPassword(
                            e.target.value
                        )
                    }
                    placeholder="Wi-Fi 비밀번호"
                    disabled={
                        working ||
                        otaWorking
                    }
                    autoComplete="new-password"
                    style={{
                        width: 180,
                        height: 34,
                        boxSizing: 'border-box',
                        border:
                            '1px solid #444',
                        borderRadius: 6,
                        background: '#0f1114',
                        color: '#fff',
                        padding: '0 10px',
                        outline: 'none',
                    }}
                />


                <div
                    style={{
                        color: '#777',
                        fontSize: 11,
                    }}
                >
                    빌드 시에만 사용하며 서버 DB에는 저장하지 않습니다.
                </div>

            </div>



            {/* ==================================================
                선택형 자동 장치 구성
            ================================================== */}

            <div
                style={{
                    background: '#171b20',
                    borderTop: '1px solid #2d333b',
                    borderBottom: '1px solid #2d333b',
                    flexShrink: 0,
                }}
            >
                <button
                    type="button"
                    onClick={() => setDevicePanelOpen(!devicePanelOpen)}
                    style={{
                        width: '100%',
                        border: 0,
                        background: '#20252b',
                        color: '#fff',
                        padding: '9px 14px',
                        fontWeight: 700,
                        textAlign: 'left',
                        cursor: 'pointer',
                    }}
                >
                    {devicePanelOpen ? '▼' : '▶'} 센서 · 스위치 · 출력 자동 구성
                    <span style={{ color: '#8ab4f8', marginLeft: 10, fontSize: 12 }}>
                        등록 {autoDevices.length}개
                    </span>
                </button>

                {devicePanelOpen && (
                    <div style={{ padding: 12 }}>
                        <div
                            style={{
                                display: 'flex',
                                gap: 8,
                                flexWrap: 'wrap',
                                alignItems: 'end',
                            }}
                        >
                            <label style={{ color: '#bbb', fontSize: 11 }}>
                                종류
                                <select
                                    value={selectedDeviceType}
                                    onChange={(e) => changeSelectedDeviceType(e.target.value)}
                                    disabled={working || otaWorking}
                                    style={{
                                        display: 'block',
                                        width: 280,
                                        height: 34,
                                        marginTop: 4,
                                        background: '#0f1114',
                                        color: '#fff',
                                        border: '1px solid #444',
                                        borderRadius: 6,
                                        padding: '0 8px',
                                    }}
                                >
                                    {[...new Set(DEVICE_CATALOG.map(item => item.category))].map(category => (
                                        <optgroup key={category} label={category}>
                                            {DEVICE_CATALOG
                                                .filter(item => item.category === category)
                                                .map(item => (
                                                    <option key={item.type} value={item.type}>
                                                        {item.label}
                                                    </option>
                                                ))}
                                        </optgroup>
                                    ))}
                                </select>
                            </label>

                            {selectedDeviceDefinition.fields.map(field => (
                                <label
                                    key={field.key}
                                    style={{
                                        color: '#bbb',
                                        fontSize: 11,
                                        flex: field.type === 'textarea' ? '1 1 100%' : '0 0 auto',
                                    }}
                                >
                                    {field.label}
                                    {field.type === 'select' ? (
                                        <select
                                            value={deviceForm[field.key] ?? ''}
                                            onChange={(e) => setDeviceForm({ ...deviceForm, [field.key]: e.target.value })}
                                            style={{
                                                display: 'block',
                                                minWidth: 110,
                                                height: 34,
                                                marginTop: 4,
                                                background: '#0f1114',
                                                color: '#fff',
                                                border: '1px solid #444',
                                                borderRadius: 6,
                                                padding: '0 8px',
                                            }}
                                        >
                                            {field.options.map(option => (
                                                <option key={option} value={option}>{option}</option>
                                            ))}
                                        </select>
                                    ) : field.type === 'textarea' ? (
                                        <textarea
                                            value={deviceForm[field.key] ?? ''}
                                            onChange={(e) => setDeviceForm({ ...deviceForm, [field.key]: e.target.value })}
                                            rows={7}
                                            spellCheck={false}
                                            style={{
                                                display: 'block',
                                                width: '100%',
                                                boxSizing: 'border-box',
                                                marginTop: 4,
                                                background: '#0f1114',
                                                color: '#fff',
                                                border: '1px solid #444',
                                                borderRadius: 6,
                                                padding: 8,
                                                fontFamily: 'monospace',
                                            }}
                                        />
                                    ) : (
                                        <input
                                            type="text"
                                            value={deviceForm[field.key] ?? ''}
                                            onChange={(e) => setDeviceForm({ ...deviceForm, [field.key]: e.target.value })}
                                            style={{
                                                display: 'block',
                                                width: field.key === 'name' ? 150 : 105,
                                                height: 34,
                                                boxSizing: 'border-box',
                                                marginTop: 4,
                                                background: '#0f1114',
                                                color: '#fff',
                                                border: '1px solid #444',
                                                borderRadius: 6,
                                                padding: '0 8px',
                                            }}
                                        />
                                    )}
                                </label>
                            ))}

                            <button
                                type="button"
                                onClick={addAutoDevice}
                                disabled={working || otaWorking || loading || !deviceId}
                                style={{
                                    height: 34,
                                    padding: '0 14px',
                                    border: '1px solid #0dcaf0',
                                    borderRadius: 6,
                                    background: '#087990',
                                    color: '#fff',
                                    fontWeight: 700,
                                    cursor: 'pointer',
                                }}
                            >
                                + 자동 추가
                            </button>
                        </div>

                        {autoDevices.length > 0 && (
                            <div
                                style={{
                                    display: 'flex',
                                    flexWrap: 'wrap',
                                    gap: 6,
                                    marginTop: 10,
                                }}
                            >
                                {autoDevices.map(device => (
                                    <div
                                        key={device.key}
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 6,
                                            background: '#252b32',
                                            border: '1px solid #3d444d',
                                            borderRadius: 6,
                                            padding: '5px 7px',
                                            color: '#ddd',
                                            fontSize: 11,
                                        }}
                                    >
                                        <span>
                                            {device.values?.name || device.label}
                                            <span style={{ color: '#888', marginLeft: 5 }}>
                                                {device.label}
                                            </span>
                                        </span>
                                        <button
                                            type="button"
                                            onClick={() => removeAutoDevice(device.key)}
                                            style={{
                                                border: 0,
                                                background: '#6c2028',
                                                color: '#fff',
                                                borderRadius: 4,
                                                cursor: 'pointer',
                                                padding: '2px 6px',
                                            }}
                                        >
                                            삭제
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}

                        <div style={{ color: '#777', fontSize: 10, marginTop: 8 }}>
                            자동 추가는 운영 기본 YAML의 KONGGOMUL AUTO DEVICES 영역만 갱신합니다. 직접 작성한 다른 YAML 영역은 유지됩니다.
                            같은 GPIO를 여러 장치에 중복 지정하지 마세요. RS485/Modbus는 센서 매뉴얼의 baud/address/register/value type 값을 확인해야 합니다.
                        </div>
                    </div>
                )}
            </div>

            {/* ==================================================
                File
            ================================================== */}

            <div
                style={{
                    height: 38,
                    display: 'flex',
                    alignItems: 'center',
                    padding: '0 14px',
                    background: '#20252b',
                    color: '#ddd',
                    fontSize: 13,
                    flexShrink: 0,
                }}
            >
                Editing site-{siteId}.yaml
            </div>


            {/* ==================================================
                Editor
            ================================================== */}

            <div
                style={{
                    flex: 1,
                    minHeight: 0,
                }}
            >

                {loading ? (

                    <div
                        style={{
                            padding: 30,
                            color: '#fff',
                        }}
                    >
                        YAML 불러오는 중...
                    </div>

                ) : (

                    <Editor
                        height="100%"
                        language="yaml"
                        theme="vs-dark"
                        value={yaml}
                        onChange={(value) =>
                            setYaml(
                                value || ''
                            )
                        }
                        options={{
                            minimap: {
                                enabled: false,
                            },

                            fontSize: 13,

                            lineNumbers: 'on',

                            automaticLayout: true,

                            tabSize: 2,

                            insertSpaces: true,

                            wordWrap: 'off',

                            scrollBeyondLastLine:
                                false,
                        }}
                    />

                )}

            </div>


            {/* ==================================================
                Output
            ================================================== */}

            {output && (

                <pre
                    style={{
                        maxHeight: 180,
                        overflow: 'auto',
                        margin: 0,
                        padding: 12,
                        background: '#0b0b0b',
                        color: '#ccc',
                        fontSize: 11,
                        whiteSpace: 'pre-wrap',
                        flexShrink: 0,
                    }}
                >
                    {output}
                </pre>

            )}


            {/* ==================================================
                Bottom
            ================================================== */}

            <div
                style={{
                    minHeight: 62,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    padding: '8px 12px',
                    background: '#20252b',
                    flexShrink: 0,
                    flexWrap: 'wrap',
                }}
            >

                {/* ----------------------------------------------
                    Message
                ---------------------------------------------- */}

                <div
                    style={{
                        color:
                            otaSuccess
                                ? '#5ee48b'
                                : buildSuccess
                                    ? '#5ee48b'
                                    : '#bbb',

                        fontSize: 13,

                        flex: 1,

                        minWidth: 180,
                    }}
                >
                    {message}
                </div>


                {/* ----------------------------------------------
                    펌웨어 초기화
                ---------------------------------------------- */}

                <button
                    type="button"
                    onClick={
                        resetFirmwareToBootstrap
                    }
                    disabled={
                        working ||
                        otaWorking ||
                        loading ||
                        !deviceId
                    }
                    title="고장난 ESP32의 USB 복구용 초기 Bootstrap 펌웨어"
                    style={{
                        padding:
                            '10px 14px',

                        border:
                            '1px solid #dc3545',

                        borderRadius: 7,

                        background:
                            '#7a1f27',

                        color: '#fff',

                        fontWeight: 700,

                        cursor:
                            working ||
                            otaWorking ||
                            loading ||
                            !deviceId
                                ? 'default'
                                : 'pointer',

                        opacity:
                            working ||
                            otaWorking ||
                            loading ||
                            !deviceId
                                ? 0.5
                                : 1,
                    }}
                >
                    펌웨어 초기화
                </button>


                {/* ----------------------------------------------
                    운영 기본 설정
                ---------------------------------------------- */}

                <button
                    type="button"
                    onClick={
                        loadOperatingBase
                    }
                    disabled={
                        working ||
                        otaWorking ||
                        loading ||
                        !deviceId
                    }
                    title="Factory Bootstrap 설치 후 센서/스위치를 추가하기 위한 운영 공통 YAML"
                    style={{
                        padding:
                            '10px 14px',

                        border:
                            '1px solid #198754',

                        borderRadius: 7,

                        background:
                            '#146c43',

                        color: '#fff',

                        fontWeight: 700,

                        cursor:
                            working ||
                            otaWorking ||
                            loading ||
                            !deviceId
                                ? 'default'
                                : 'pointer',

                        opacity:
                            working ||
                            otaWorking ||
                            loading ||
                            !deviceId
                                ? 0.5
                                : 1,
                    }}
                >
                    운영 기본 설정
                </button>


                {/* ----------------------------------------------
                    Validate
                ---------------------------------------------- */}

                <button
                    type="button"
                    onClick={
                        validateYaml
                    }
                    disabled={
                        working ||
                        otaWorking ||
                        loading
                    }
                    style={{
                        padding:
                            '10px 14px',

                        border:
                            '1px solid #555',

                        borderRadius: 7,

                        background:
                            '#30363d',

                        color: '#fff',

                        cursor:
                            working ||
                            otaWorking
                                ? 'default'
                                : 'pointer',
                    }}
                >
                    Validate
                </button>


                {/* ----------------------------------------------
                    Save
                ---------------------------------------------- */}

                <button
                    type="button"
                    onClick={
                        saveYaml
                    }
                    disabled={
                        working ||
                        otaWorking ||
                        loading
                    }
                    style={{
                        padding:
                            '10px 14px',

                        border:
                            '1px solid #555',

                        borderRadius: 7,

                        background:
                            '#30363d',

                        color: '#fff',

                        cursor:
                            working ||
                            otaWorking
                                ? 'default'
                                : 'pointer',
                    }}
                >
                    Save
                </button>


                {/* ----------------------------------------------
                    Build
                ---------------------------------------------- */}

                <button
                    type="button"
                    onClick={
                        buildFirmware
                    }
                    disabled={
                        working ||
                        otaWorking ||
                        loading
                    }
                    style={{
                        padding:
                            '10px 16px',

                        border: 'none',

                        borderRadius: 7,

                        background:
                            '#08a4c7',

                        color: '#fff',

                        fontWeight: 600,

                        cursor:
                            working ||
                            otaWorking
                                ? 'default'
                                : 'pointer',
                    }}
                >
                    {working
                        ? 'Building...'
                        : 'Build'}
                </button>


                {/* ----------------------------------------------
                    Factory Download
                ---------------------------------------------- */}

                {buildSuccess && factoryFirmwareUrl && (

                    <button
                        type="button"
                        onClick={
                            downloadFactoryFirmware
                        }
                        disabled={
                            otaWorking
                        }
                        style={{
                            padding:
                                '10px 14px',

                            border:
                                '1px solid #6f42c1',

                            borderRadius: 7,

                            background:
                                '#6f42c1',

                            color: '#fff',

                            fontWeight: 600,

                            cursor:
                                otaWorking
                                    ? 'default'
                                    : 'pointer',
                        }}
                    >
                        Factory Download
                    </button>

                )}


                {/* ----------------------------------------------
                    Factory 펌업
                ---------------------------------------------- */}

                {buildSuccess &&
                    factoryFirmwareUrl &&
                    factoryManifestUrl && (

                    <esp-web-install-button
                        manifest={
                            factoryManifestUrl
                        }
                    >
                        <button
                            slot="activate"
                            type="button"
                            disabled={
                                otaWorking ||
                                working ||
                                loading
                            }
                            style={{
                                padding:
                                    '10px 14px',

                                border:
                                    '1px solid #6f42c1',

                                borderRadius: 7,

                                background:
                                    '#4d2f87',

                                color: '#fff',

                                fontWeight: 700,

                                cursor:
                                    otaWorking ||
                                    working ||
                                    loading
                                        ? 'default'
                                        : 'pointer',
                            }}
                        >
                            Factory 펌업
                        </button>

                        <span
                            slot="unsupported"
                            style={{
                                color: '#ffb3b3',
                                fontSize: 12,
                            }}
                        >
                            이 브라우저는 USB 펌업을 지원하지 않습니다.
                        </span>

                        <span
                            slot="not-allowed"
                            style={{
                                color: '#ffb3b3',
                                fontSize: 12,
                            }}
                        >
                            HTTPS 환경에서만 USB 펌업을 사용할 수 있습니다.
                        </span>
                    </esp-web-install-button>

                )}


                {/* ----------------------------------------------
                    Download
                ---------------------------------------------- */}

                {buildSuccess && (

                    <button
                        type="button"
                        onClick={
                            downloadFirmware
                        }
                        disabled={
                            otaWorking
                        }
                        style={{
                            padding:
                                '10px 14px',

                            border: 'none',

                            borderRadius: 7,

                            background:
                                '#198754',

                            color: '#fff',

                            fontWeight: 600,

                            cursor:
                                otaWorking
                                    ? 'default'
                                    : 'pointer',
                        }}
                    >
                        Download
                    </button>

                )}



                {/* ----------------------------------------------
                    ★ 실제 OTA 업데이트
                ---------------------------------------------- */}

                {buildSuccess && (

                    <button
                        type="button"
                        onClick={
                            uploadOta
                        }
                        disabled={
                            otaWorking ||
                            working ||
                            loading
                        }
                        style={{
                            padding:
                                '10px 18px',

                            border:
                                '1px solid #ff8a00',

                            borderRadius: 7,

                            background:
                                otaWorking
                                    ? '#6b3d00'
                                    : '#ff8a00',

                            color: '#fff',

                            fontWeight: 700,

                            cursor:
                                otaWorking ||
                                working ||
                                loading
                                    ? 'default'
                                    : 'pointer',

                            boxShadow:
                                otaWorking
                                    ? 'none'
                                    : '0 0 0 1px rgba(255,138,0,0.15)',
                        }}
                    >
                        {otaWorking
                            ? 'OTA 업데이트 중...'
                            : 'OTA 업데이트'}
                    </button>

                )}

            </div>

        </div>
    );
}
// 기존 빌더 본문은 그대로 두고, URL 모드에 따라 별도 양액기 컴포넌트만 선택합니다.
export function NutrientEntry() {
    const [params] = useSearchParams();
    const navigate = useNavigate();
    const siteId = Number(params.get('site_id') || 0);
    const deviceId = Number(params.get('device_id') || 0);
    const [creating, setCreating] = useState(false);
    const [loadingList, setLoadingList] = useState(true);
    const [existing, setExisting] = useState([]);
    const [error, setError] = useState('');
    const storageKey = `konggomul_nutrient_device_${siteId}`;
    useEffect(() => {
        let cancelled = false;
        if (!siteId) { setLoadingList(false); return; }
        setLoadingList(true);
        fetch(`/api/seller/smartfarm/esp32/devices?site_id=${siteId}`, {credentials:'include',cache:'no-store'})
            .then(async r => { const d = await r.json(); if (!r.ok || !d.ok) throw new Error(d.error || '장치 목록 조회 실패'); return d.devices || []; })
            .then(devices => { if (!cancelled) setExisting(devices.filter(d => isNutrientDevice(d, siteId))); })
            .catch(e => { if (!cancelled) setError(e.message || '장치 목록 조회 실패'); })
            .finally(() => { if (!cancelled) setLoadingList(false); });
        return () => { cancelled = true; };
    }, [siteId]);
    const selected = existing.some(d => Number(d.device_id) === deviceId);
    if (deviceId > 0 && (selected || (!loadingList && !error && existing.length >= 0))) {
        // 명시적으로 양액기 URL에 진입한 장치는 전용 빌더에서 엽니다.
        // 실제 장치 소유권은 기존 서버 API와 셀러 인증에서 검사합니다.
        return <Esp32NutrientBuilder />;
    }
    const create = async () => {
        if (!siteId || creating) return;
        const name = window.prompt('새 양액기 ESP32 이름', '양액기 ESP32');
        if (name === null) return;
        try {
            setCreating(true); setError('');
            const res = await fetch('/api/seller/smartfarm/esp32/devices', {
                method:'POST', credentials:'include', headers:{'Content-Type':'application/json'},
                body:JSON.stringify({site_id:siteId,device_name:name.trim().includes('양액') || /nutrient/i.test(name.trim()) ? name.trim() : `양액기 ${name.trim() || 'ESP32'}`})
            });
            const data = await res.json();
            const id = Number(data.device_id || 0);
            if (!res.ok || !data.ok || id <= 0) throw new Error(data.error || '장치 생성 실패');
            window.localStorage.setItem(storageKey, String(id));
            let ids = []; try { ids = JSON.parse(window.localStorage.getItem(`konggomul_nutrient_devices_${siteId}`) || '[]'); } catch (_) {}
            window.localStorage.setItem(`konggomul_nutrient_devices_${siteId}`, JSON.stringify([...new Set([...(Array.isArray(ids) ? ids : []), id])]));
            navigate(`/seller/smartfarm/esp32/nutrient-builder?site_id=${siteId}&device_id=${id}`, {replace:true});
        } catch (e) { setError(e.message || '장치 생성 실패'); }
        finally { setCreating(false); }
    };
    return <div style={{minHeight:'100vh',background:'#15191f',color:'#fff',padding:28,fontFamily:'sans-serif'}}>
        <h2>콩꼬물 · ESP32 양액기 전용 빌더</h2>
        <p>농장 ID: {siteId || '없음'} · 기존 양액기 장치를 선택하거나 새로 등록할 수 있습니다.</p>
        {loadingList && <p>양액기 장치 목록 불러오는 중...</p>}
        {!loadingList && existing.map(d => <button key={d.device_id} type="button"
            onClick={() => navigate(`/seller/smartfarm/esp32/nutrient-builder?site_id=${siteId}&device_id=${d.device_id}`)}
            style={{display:'block',margin:'8px 0',padding:'10px 16px',background:'#253744',color:'#fff',border:'1px solid #587487',borderRadius:6,cursor:'pointer'}}>
            {d.device_name || `양액기 ESP32 #${d.device_id}`} (#{d.device_id}) · 편집
        </button>)}
        <button type="button" onClick={create} disabled={creating || !siteId}
            style={{background:'#087990',color:'white',border:0,borderRadius:6,padding:'12px 18px',cursor:'pointer',marginTop:12}}>
            {creating ? '등록 중...' : '+ 양액기 전용 ESP32 생성'}
        </button>
        {error && <p style={{color:'#ff8585'}}>{error}</p>}
    </div>;
}
export default function Esp32Builder() {
    const [params] = useSearchParams();
    const siteId = Number(params.get('site_id') || 0);
    const deviceId = Number(params.get('device_id') || 0);
    const nutrientId = Number(window.localStorage.getItem(`konggomul_nutrient_device_${siteId}`) || 0);
    let knownIds = []; try { knownIds = JSON.parse(window.localStorage.getItem(`konggomul_nutrient_devices_${siteId}`) || '[]'); } catch (_) {}
    return params.get('mode') === 'nutrient' || (deviceId > 0 && (deviceId === nutrientId || (Array.isArray(knownIds) && knownIds.some(x => Number(x) === deviceId))))
        ? <NutrientEntry />
        : <OriginalEsp32Builder />;
}
